import { useCallback, useEffect, useRef, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { theme_scheme as setThemeScheme } from "../../store/setting/actions";
import * as SettingSelector from "../../store/setting/selectors";

export const AnimatedThemeToggler = ({
  className,
  duration = 400,
  ...props
}) => {
  const buttonRef = useRef(null);
  const dispatch = useDispatch();
  const themeScheme = useSelector(SettingSelector.theme_scheme);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const darkModeActive =
      document.body.classList.contains("dark") ||
      themeScheme === "dark" ||
      (themeScheme === "auto" &&
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    setIsDark(darkModeActive);
    document.documentElement.classList.toggle("dark", darkModeActive);
    document.body.classList.toggle("dark", darkModeActive);
    localStorage.setItem("theme", darkModeActive ? "dark" : "light");
  }, [themeScheme]);

  const toggleTheme = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const { top, left, width, height } = button.getBoundingClientRect();
    const x = left + width / 2;
    const y = top + height / 2;
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    const maxRadius = Math.hypot(
      Math.max(x, viewportWidth - x),
      Math.max(y, viewportHeight - y),
    );

    const applyTheme = () => {
      const nextTheme = isDark ? "light" : "dark";
      setIsDark(nextTheme === "dark");
      document.documentElement.classList.toggle("dark", nextTheme === "dark");
      document.body.classList.toggle("dark", nextTheme === "dark");
      localStorage.setItem("theme", nextTheme);
      dispatch(setThemeScheme(nextTheme));
    };

    if (typeof document.startViewTransition !== "function") {
      applyTheme();
      return;
    }

    const transition = document.startViewTransition(() => {
      flushSync(applyTheme);
    });

    const ready = transition?.ready;
    if (ready && typeof ready.then === "function") {
      ready.then(() => {
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${maxRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration,
            easing: "ease-in-out",
            pseudoElement: "::view-transition-new(root)",
          },
        );
      });
    }
  }, [isDark, duration]);

  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={toggleTheme}
      className={className}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      {...props}
    >
      {isDark ? <Sun /> : <Moon />}
      <span className="sr-only">Toggle theme</span>
    </button>
  );
};
